<template>
  <h2>{{ t('resultingDamages') }}</h2>
  <div v-if="damages" class="relative mt-4">
    <div
      v-for="(damage, i) in damages"
      :key="i"
      class="border-primary/40 relative flex gap-4 border-b py-2"
    >
      <div class="grid flex-1 grid-cols-8 items-center gap-4">
        <div class="col-span-1">
          {{ formatDate(damage.createdDate, dateFormat) }}
        </div>
        <div
          v-if="booking.allocations.length > 1 && damage.car.vehicleType"
          class="col-span-3 flex gap-2"
        >
          <LicencePlate>{{ damage.car.licencePlate }}</LicencePlate>
          <div class="overlow-hidden truncate whitespace-nowrap">
            {{ damage.car.vehicleType.make.name }} {{ damage.car.vehicleType.model }}
          </div>
        </div>
        <div
          class="overlow-hidden truncate whitespace-nowrap"
          :class="{
            'col-span-7': booking.allocations.length <= 1,
            'col-span-4': booking.allocations.length > 1,
          }"
        >
          {{ damage.description }}
        </div>
      </div>
      <div class="flex justify-end">
        <CVButton class="btn-sm" outline @click.prevent="viewDamage(damage.id)">{{
          t('viewDamage')
        }}</CVButton>
      </div>
    </div>
  </div>

  <EntityListExceptions
    :status="status"
    :error="error"
    :items-count="damages?.length ?? 0"
    :no-items-text="t('noDamagesFound')"
    small
  />

  <div class="mt-5 flex justify-end gap-4">
    <CVButton
      v-if="booking.hasUnhandledDamages"
      variant="default"
      class="max-sm:w-full"
      :is-loading="isSaving"
      @click.prevent="allDamagesHandled"
      >{{ t('allDamagesHandled') }}</CVButton
    >
    <CVButton
      variant="secondary"
      class="max-sm:w-full"
      :disabled="isSaving"
      @click.prevent="
        $router.push({
          name: 'bookingEditDamages',
          params: { id: props.booking.id },
          query: { tab: 'NEW' },
        })
      "
      >{{ t('editDamages') }}</CVButton
    >
  </div>

  <DamageViewer
    v-if="viewedDamage"
    :key="viewedDamage?.id"
    v-model="showModal"
    :damage="viewedDamage"
  />
</template>

<script lang="ts" setup>
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { useDamages } from '@/queries/use-damages';
import { computed, ref } from 'vue';
import DamageViewer from './DamageViewer.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { Alert } from '@/services/alert.service';
import type { Booking } from '@/entities/bookings/booking.entity';
import CVButton from '@/components/buttons/CVButton.vue';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import LicencePlate from '@/components/LicencePlate.vue';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();

const { isPending: isSaving, mutateAsync: updateBooking } = useUpdateBooking();

const dateFormat = 'dd.MM.yy';
const showModal = ref(false);
const viewedDamageId = ref<string | null>(null);

const { data: damages, status, error } = useDamages({ bookingIds: [props.booking.id] });

const viewDamage = (damageId: string) => {
  viewedDamageId.value = damageId;
  showModal.value = true;
};

const allDamagesHandled = async () => {
  const { isConfirmed } = await Alert.fire({
    title: t('allDamagesHandledTitle'),
    text: t('allDamagesHandledText'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('yes'),
    cancelButtonText: t('no'),
  });

  if (!isConfirmed) return;
  await updateBooking({
    id: props.booking.id,
    booking: { hasUnhandledDamages: false },
  });
};

const viewedDamage = computed(() => damages.value?.find((d) => d.id === viewedDamageId.value));
</script>

<i18n lang="json">
{
  "en": {
    "resultingDamages": "Resulting Damages",
    "viewDamage": "View Damages",
    "noDamagesFound": "This booking has no damages",
    "editDamages": "Edit Damages",
    "allDamagesHandled": "All damages handled",
    "allDamagesHandledTitle": "Are all damages handled?",
    "allDamagesHandledText": "Please make sure, that all damages are handled (invoiced, paid, etc.) before you continue. Do you want to continue?"
  },
  "de": {
    "resultingDamages": "Entstandene Schäden",
    "viewDamage": "Schaden ansehen",
    "noDamagesFound": "Dieser Buchung sind keine Schäden zugeordnet",
    "editDamages": "Schäden bearbeiten",
    "allDamagesHandled": "Alle Schäden bearbeitet",
    "allDamagesHandledTitle": "Sind alle Schäden bearbeitet?",
    "allDamagesHandledText": "Bitte stelle sicher, dass alle Schäden bearbeitet sind (abgerechnet, bezahlt, etc.) bevor du fortfährst. Möchtest du fortfahren?"
  },
  "es": {
    "resultingDamages": "Daños resultantes",
    "viewDamage": "Ver daños",
    "noDamagesFound": "Esta reserva no tiene daños",
    "editDamages": "Editar daños",
    "allDamagesHandled": "Todos los daños gestionados",
    "allDamagesHandledTitle": "¿Están gestionados todos los daños?",
    "allDamagesHandledText": "Asegúrese de que todos los daños estén gestionados (facturados, pagados, etc.) antes de continuar. ¿Desea continuar?"
  }
}
</i18n>
